import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usuarioService from '../services/UsuarioService';
import RegionalService from '../services/RegionalService';
import PasswordInputWithToggle from './common/PasswordInputWithToggle';

const CambiarContrasena = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ current: '', next: '', repeat: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.next !== form.repeat) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    if (form.next.length < 12) {
      setError('La contraseña nueva debe tener al menos 12 caracteres.');
      return;
    }
    setLoading(true);
    try {
      const response = await usuarioService.updatePassword(form.current, form.next, form.repeat);
      const roleName = response?.nombreRol || response?.NombreRol || user?.roleName || user?.role || '';
      const regionales = response?.regionales || response?.Regionales || RegionalService.getRegionales();
      const areas = response?.areas || response?.Areas || [];
      const materias = response?.materias || response?.Materias || [];
      const featureFlags = response?.ffRegional || response?.FFRegional || RegionalService.getFeatureFlags();

      RegionalService.setRegionalData({ regionales, areas, materias });
      RegionalService.setFeatureFlags(Array.isArray(featureFlags) ? featureFlags : []);
      setSuccess(true);
      updateUser({
        token: response?.token || response?.Token || user?.token,
        refreshToken: response?.refreshToken || response?.RefreshToken || user?.refreshToken,
        requirePasswordChange: Boolean(response?.requirePasswordChange ?? response?.RequirePasswordChange ?? false),
        roleId: response?.idRol || response?.IdRol || user?.roleId,
        roleName,
        role: roleName,
        availableAccessContexts: response?.contextosAcceso || response?.ContextosAcceso || user?.availableAccessContexts || [],
        currentAccessContext: response?.contextoAccesoActual || response?.ContextoAccesoActual || user?.currentAccessContext || null,
        requiresAccessContextSelection: Boolean(response?.requiereSeleccionContexto ?? response?.RequiereSeleccionContexto ?? user?.requiresAccessContextSelection ?? false)
      });
      setTimeout(() => {
        const normalizedRoleName = String(roleName).toLowerCase();
        navigate(normalizedRoleName.includes('admin') ? '/admin-dashboard' : '/dashboard', { replace: true });
      }, 1500);
    } catch (err) {
      setError(err.message || 'Error al cambiar la contraseña. Verificá los datos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.icon}>🔐</div>
        <h2 style={styles.title}>Cambio de contraseña requerido</h2>
        <p style={styles.subtitle}>
          Tu cuenta requiere que establezcas una nueva contraseña antes de continuar.
        </p>

        {success ? (
          <div style={styles.successMsg}>
            ✅ Contraseña actualizada correctamente. Redirigiendo...
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.label}>Contraseña actual</label>
            <PasswordInputWithToggle
              name="current"
              value={form.current}
              onChange={handleChange}
              style={styles.input}
              required
              autoComplete="current-password"
              label="contraseña actual"
            />

            <label style={styles.label}>Nueva contraseña</label>
            <PasswordInputWithToggle
              name="next"
              value={form.next}
              onChange={handleChange}
              style={styles.input}
              required
              autoComplete="new-password"
              placeholder="Mínimo 12 caracteres"
              label="nueva contraseña"
            />

            <label style={styles.label}>Repetir nueva contraseña</label>
            <PasswordInputWithToggle
              name="repeat"
              value={form.repeat}
              onChange={handleChange}
              style={styles.input}
              required
              autoComplete="new-password"
              label="repetir nueva contraseña"
            />

            {error && <div style={styles.errorMsg}>{error}</div>}

            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? 'Guardando...' : 'Cambiar contraseña'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '2.5rem 2rem',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    textAlign: 'center',
  },
  icon: {
    fontSize: '3rem',
    marginBottom: '0.75rem',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: '700',
    color: '#1e3a8a',
    marginBottom: '0.5rem',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#6b7280',
    marginBottom: '1.5rem',
    lineHeight: '1.5',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    textAlign: 'left',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#374151',
    marginTop: '0.5rem',
  },
  input: {
    padding: '0.65rem 0.85rem',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  button: {
    marginTop: '1.25rem',
    padding: '0.75rem',
    background: '#1e3a8a',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  errorMsg: {
    marginTop: '0.5rem',
    padding: '0.6rem 0.85rem',
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    color: '#dc2626',
    fontSize: '0.85rem',
  },
  successMsg: {
    padding: '1rem',
    background: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '8px',
    color: '#16a34a',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
};

export default CambiarContrasena;
